import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { ArrowForwardRounded, AutoAwesomeRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import ObjectIcon from "../components/ObjectIcon";
import { Button } from "../components/Buttons";
import { useObjects } from "../context/ObjectsContext";
import { applySalesCrmTemplate, getDashboardSummary } from "../api";
import { formatRelativeTime } from "../utils/format";
import { apiError } from "../utils/meta";

/* ------------------------------------------------------------------ */
/* Onboarding — shown while the org has no objects yet                  */
/* ------------------------------------------------------------------ */

function BlankCanvas({ onTemplateApplied }) {
    const [applying, setApplying] = useState(false);

    async function useTemplate() {
        setApplying(true);
        try {
            const { data } = await applySalesCrmTemplate();
            toast.success(
                `Created ${data?.created?.length ?? 4} objects from the Sales CRM template`,
            );
            await onTemplateApplied();
        } catch (err) {
            toast.error(apiError(err, "Could not apply the template"));
        } finally {
            setApplying(false);
        }
    }

    return (
        <Stack sx={{ alignItems: "center", pt: { xs: 4, md: 10 }, gap: 5 }}>
            <Stack sx={{ alignItems: "center", gap: 1.5, maxWidth: 560 }}>
                <Typography
                    sx={{
                        fontFamily: "'Newsreader', Georgia, serif",
                        fontSize: { xs: 30, md: 40 },
                        fontWeight: 500,
                        letterSpacing: "-0.5px",
                        textAlign: "center",
                        lineHeight: 1.15,
                    }}
                >
                    Your workspace is a blank canvas.
                </Typography>
                <Typography
                    sx={{
                        color: "var(--text-secondary)",
                        fontSize: 15,
                        textAlign: "center",
                        lineHeight: 1.6,
                    }}
                >
                    Tessera has no built-in objects. Define what your team
                    tracks — start from a template, or shape everything
                    yourself.
                </Typography>
            </Stack>

            <Stack
                sx={{
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2.5,
                    width: "100%",
                    maxWidth: 820,
                }}
            >
                <Card sx={{ flex: 1, border: "1px solid var(--border-mid)" }}>
                    <CardContent sx={{ p: 3.5 }}>
                        <Stack sx={{ gap: 2, alignItems: "flex-start" }}>
                            <Stack
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "3px",
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AutoAwesomeRounded sx={{ fontSize: 20 }} />
                            </Stack>
                            <Stack sx={{ gap: 0.5 }}>
                                <Typography
                                    sx={{ fontWeight: 600, fontSize: 16 }}
                                >
                                    Start from a template
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        color: "var(--text-secondary)",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Sales CRM — Leads, Contacts, Companies and
                                    Deals, ready to customise. They become your
                                    objects; change anything.
                                </Typography>
                            </Stack>
                            <Button
                                variant="contained"
                                color="primary"
                                loading={applying}
                                onClick={useTemplate}
                            >
                                Use Sales CRM template
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ flex: 1, border: "1px solid var(--border-mid)" }}>
                    <CardContent sx={{ p: 3.5 }}>
                        <Stack sx={{ gap: 2, alignItems: "flex-start" }}>
                            <Stack
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "3px",
                                    background: "var(--bg-tint-soft)",
                                    color: "var(--text)",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ObjectIcon name="cube" size={20} />
                            </Stack>
                            <Stack sx={{ gap: 0.5 }}>
                                <Typography
                                    sx={{ fontWeight: 600, fontSize: 16 }}
                                >
                                    Build your own objects
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        color: "var(--text-secondary)",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Model exactly what your business tracks —
                                    objects, fields, pipelines and workflows,
                                    from scratch.
                                </Typography>
                            </Stack>
                            <Button
                                variant="contained"
                                color="primary"
                                component={Link}
                                to="/admin/objects"
                                
                                startIcon={<ArrowForwardRounded />}
                            >
                                Open the object builder
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* Stat tile — white card, hairline border, serif number                */
/* ------------------------------------------------------------------ */

function StatTile({ label, value, sub }) {
    return (
        <Stack
            sx={{
                flex: 1,
                minWidth: 180,
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-mid)",
                borderRadius: "3px",
                p: "20px 22px",
                gap: 0.75,
            }}
        >
            <Typography
                sx={{
                    fontSize: 11,
                    letterSpacing: "1.5px",
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: "'Newsreader', Georgia, serif",
                    fontSize: 34,
                    fontWeight: 500,
                    letterSpacing: "-0.5px",
                    lineHeight: 1.1,
                }}
            >
                {value}
            </Typography>
            {sub && (
                <Typography
                    sx={{ fontSize: 12.5, color: "var(--text-secondary)" }}
                >
                    {sub}
                </Typography>
            )}
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* Dashboard                                                            */
/* ------------------------------------------------------------------ */

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
};

export default function Dashboard() {
    const navigate = useNavigate();
    const { objects, loading: objectsLoading, refresh } = useObjects();
    const [summary, setSummary] = useState(null);

    const hasObjects = objects.length > 0;
    const firstName = localStorage.getItem("firstName") || "";

    useEffect(() => {
        if (!hasObjects) return;
        (async () => {
            try {
                const { data } = await getDashboardSummary();
                setSummary(data ?? {});
            } catch (error) {
                console.error("Failed to load dashboard", { error });
            }
        })();
    }, [hasObjects]);

    if (objectsLoading) {
        return (
            <Stack sx={{ alignItems: "center", py: 12 }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (!hasObjects) {
        return <BlankCanvas onTemplateApplied={refresh} />;
    }

    const totals = summary?.totals ?? {};
    const runs = summary?.runs ?? {};
    const summaryObjects = summary?.objects ?? [];
    const recent = summary?.recent ?? [];

    return (
        <Stack sx={{ gap: 3.5 }}>
            <Stack sx={{ gap: 0.5 }}>
                <Typography
                    sx={{
                        fontFamily: "'Newsreader', Georgia, serif",
                        fontSize: { xs: 26, md: 32 },
                        fontWeight: 500,
                        letterSpacing: "-0.5px",
                        lineHeight: 1.15,
                    }}
                >
                    {greeting()}
                    {firstName ? `, ${firstName}` : ""}.
                </Typography>
                <Typography
                    sx={{ fontSize: 14, color: "var(--text-secondary)" }}
                >
                    Where your workspace stands today.
                </Typography>
            </Stack>

            <Stack sx={{ flexDirection: "row", flexWrap: "wrap", gap: 2 }}>
                <StatTile
                    label="Total records"
                    value={(totals.records ?? 0).toLocaleString("en-IN")}
                />
                <StatTile label="Users" value={totals.users ?? 0} />
                <StatTile
                    label="Workflows"
                    value={totals.workflows ?? 0}
                    sub={`${totals.activeWorkflows ?? 0} active`}
                />
                <StatTile
                    label="Automation runs · 7 days"
                    value={(runs.last7 ?? 0).toLocaleString("en-IN")}
                    sub={`${runs.error7 ?? 0} errors`}
                />
            </Stack>

            <Stack sx={{ gap: 1.5 }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        letterSpacing: "2px",
                        fontWeight: 700,
                        color: "var(--text-tertiary)",
                    }}
                >
                    OBJECTS
                </Typography>
                <Stack sx={{ flexDirection: "row", flexWrap: "wrap", gap: 2 }}>
                    {summaryObjects.map((obj) => (
                        <Stack
                            key={obj.id}
                            onClick={() => navigate(`/o/${obj.slug}`)}
                            sx={{
                                flex: "1 1 220px",
                                maxWidth: 320,
                                background: "var(--bg-secondary)",
                                border: "1px solid var(--border-mid)",
                                borderRadius: "3px",
                                p: "18px 20px",
                                gap: 1.5,
                                cursor: "pointer",
                                transition: "border-color 0.15s",
                                "&:hover": {
                                    borderColor: "var(--border-strong)",
                                },
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1.25,
                                }}
                            >
                                <Stack
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: "3px",
                                        background: "var(--bg-tint-soft)",
                                        color: "var(--text)",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <ObjectIcon name={obj.icon} size={17} />
                                </Stack>
                                <Typography
                                    sx={{ fontSize: 14.5, fontWeight: 600 }}
                                >
                                    {obj.name}
                                </Typography>
                            </Stack>
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "baseline",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily:
                                            "'Newsreader', Georgia, serif",
                                        fontSize: 28,
                                        fontWeight: 500,
                                        lineHeight: 1,
                                    }}
                                >
                                    {(obj.count ?? 0).toLocaleString("en-IN")}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12.5,
                                        color: "var(--accent)",
                                        fontWeight: 600,
                                    }}
                                >
                                    +{obj.last30 ?? 0} in 30 days
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Stack>

            <Stack sx={{ gap: 1.5 }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        letterSpacing: "2px",
                        fontWeight: 700,
                        color: "var(--text-tertiary)",
                    }}
                >
                    RECENT RECORDS
                </Typography>
                <Stack
                    sx={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-mid)",
                        borderRadius: "3px",
                    }}
                >
                    {recent.length === 0 && (
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: "var(--text-secondary)",
                                textAlign: "center",
                                py: 5,
                            }}
                        >
                            Nothing yet — new records will show up here.
                        </Typography>
                    )}
                    {recent.map((rec, i) => (
                        <Stack
                            key={rec.id}
                            onClick={() => navigate(`/o/${rec.objectSlug}`)}
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                p: "12px 18px",
                                borderTop:
                                    i === 0
                                        ? "none"
                                        : "1px solid var(--border)",
                                cursor: "pointer",
                                "&:hover": {
                                    background: "var(--bg-tint-soft)",
                                },
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1.5,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {rec.title || "Untitled"}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={rec.objectName}
                                    sx={{
                                        height: 20,
                                        fontSize: 11,
                                        background: "var(--bg-tint-soft)",
                                        border: "1px solid var(--border-mid)",
                                        color: "var(--text-secondary)",
                                    }}
                                />
                            </Stack>
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 2,
                                    flexShrink: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: "var(--text-secondary)",
                                    }}
                                >
                                    {rec.ownerName || "Unassigned"}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12.5,
                                        color: "var(--text-tertiary)",
                                        width: 76,
                                        textAlign: "right",
                                    }}
                                >
                                    {formatRelativeTime(rec.createdAt)}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Stack>
    );
}
